/**
 * HTML内容处理工具
 * 根据项目文档说明，处理AI生成的HTML内容中的特殊标签
 */

/**
 * 从HTML内容中提取纯HTML部分
 * 移除可能存在的 ===THINKING_START===、===THINKING_END===、===HTML_RESULT_START===、===HTML_RESULT_END=== 标签
 */
export function extractHtmlContent(content: string): string {
  if (!content) return '';

  // 移除思考过程标签内容
  let processedContent = content
    .replace(/===THINKING_START===([\s\S]*?)===THINKING_END===/g, '')
    .trim();

  // 如果存在HTML结果标签，只取其中的内容
  const htmlResultMatch = processedContent.match(/===HTML_RESULT_START===([\s\S]*?)===HTML_RESULT_END===/);
  if (htmlResultMatch) {
    processedContent = htmlResultMatch[1].trim();
  }

  return processedContent;
}

/**
 * 检测内容类型
 */
export function detectContentType(content: string): 'html' | 'markdown' {
  if (!content) return 'markdown';
  
  const trimmedContent = content.trim();
  
  // 检查是否包含HTML标签
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(trimmedContent);
  const startsWithHtml = trimmedContent.toLowerCase().startsWith('<!doctype html') || 
                        trimmedContent.toLowerCase().startsWith('<html');
  
  return (hasHtmlTags || startsWithHtml) ? 'html' : 'markdown';
}

/**
 * 从完整HTML文档中提取body内容
 */
export function extractBodyContent(htmlContent: string): string {
  if (!htmlContent) return '';
  
  // 提取body标签内的内容
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    return bodyMatch[1].trim();
  }
  
  // 如果没有body标签，检查是否为完整HTML文档
  if (htmlContent.includes('<!DOCTYPE') || htmlContent.includes('<html')) {
    // 完整HTML文档但没有body标签，返回去掉html和head部分的内容
    const withoutDoctype = htmlContent.replace(/<!DOCTYPE[^>]*>/i, '');
    const withoutHtml = withoutDoctype.replace(/<\/?html[^>]*>/gi, '');
    const withoutHead = withoutHtml.replace(/<head[\s\S]*?<\/head>/gi, '');
    return withoutHead.trim();
  }
  
  // 如果是片段HTML，直接返回
  return htmlContent;
}

/**
 * 处理模型内容
 * 统一处理从API返回的模型内容，支持HTML和Markdown两种格式
 */
export function processModelContent(content: string): {
  processedContent: string;
  contentType: 'html' | 'markdown';
} {
  if (!content) {
    return {
      processedContent: '',
      contentType: 'markdown'
    };
  }

  // 1. 首先提取HTML内容（移除AI思考标签）
  const extractedContent = extractHtmlContent(content);
  
  // 2. 检测内容类型
  const contentType = detectContentType(extractedContent);
  
  // 3. 如果是HTML，提取body内容
  let processedContent = extractedContent;
  if (contentType === 'html') {
    processedContent = extractBodyContent(extractedContent);
  }

  return {
    processedContent,
    contentType
  };
}